import type { ReactNode } from "react";

import { ActionBar } from "./ActionBar";
import { I } from "./icons";
import { Sheet } from "./Sheet";

/**
 * A destructive step asked in a Sheet rather than `window.confirm()`, whose dialog is unstyled,
 * blocks the page and ignores the theme.
 *
 * When to use: one question before something that cannot be taken back. Render it only while
 * the question is open; `confirm.onClick` should close it (or navigate away).
 */
export function ConfirmSheet({
  title,
  sub,
  confirm,
  cancel = "Cancel",
  onClose,
  children,
}: {
  title: ReactNode;
  sub?: ReactNode;
  /** `icon`: the confirm's glyph — a trash can by default, `null` for none (Stop is not a delete) */
  confirm: { label: string; onClick: () => void; busy?: boolean; icon?: ReactNode | null };
  cancel?: string;
  onClose: () => void;
  children?: ReactNode;
}) {
  return (
    <Sheet
      title={title}
      sub={sub}
      onClose={onClose}
      footer={
        <ActionBar
          inSheet
          secondary={{ label: cancel, onClick: onClose }}
          primary={{
            label: confirm.label,
            onClick: confirm.onClick,
            busy: confirm.busy,
            icon: confirm.icon === undefined ? I.trash : (confirm.icon ?? undefined),
            tone: "danger",
          }}
        />
      }
    >
      {children ?? null}
    </Sheet>
  );
}
